interface SkeletonProps {
  className?: string
  style?: React.CSSProperties
}

export function Skeleton({ className = "", style }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse rounded bg-gold-500/30 ${className}`}
      style={style}
    />
  )
}

export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
      {/* Header */}
      <div className="border-b border-gold-500/50 bg-cream-50 px-5 py-4">
        <div className="flex gap-8">
          {Array.from({ length: columns }, (_, i) => (
            <Skeleton key={i} className="h-3 w-20" style={{ animationDelay: `${i * 50}ms` }} />
          ))}
        </div>
      </div>
      {/* Rows */}
      <div className="divide-y divide-gold-500/30">
        {Array.from({ length: rows }, (_, rowIndex) => (
          <div key={rowIndex} className="flex items-center gap-8 px-5 py-4">
            {Array.from({ length: columns }, (_, colIndex) => (
              <Skeleton
                key={colIndex}
                className={`h-3 ${colIndex === 0 ? "w-32" : colIndex === columns - 1 ? "w-16" : "w-24"}`}
                style={{ animationDelay: `${(rowIndex * columns + colIndex) * 30}ms` }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export function StatCardSkeleton() {
  return (
    <div className="flex flex-1 flex-col justify-between rounded-[10px] border border-gold-500 bg-white px-6 py-4 h-[140px]">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="h-8 w-32" />
    </div>
  )
}

export function StatCardsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row">
      {Array.from({ length: count }, (_, i) => (
        <StatCardSkeleton key={i} />
      ))}
    </div>
  )
}

export function TabsSkeleton({ count = 2 }: { count?: number }) {
  return (
    <div className="flex gap-1 overflow-x-auto rounded-full border border-ink/10 bg-white p-2 w-fit">
      {Array.from({ length: count }, (_, i) => (
        <Skeleton
          key={i}
          className="h-10 w-32 rounded-full"
          style={{ animationDelay: `${i * 100}ms` }}
        />
      ))}
    </div>
  )
}

export function FiltersSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="flex flex-wrap gap-3">
      {Array.from({ length: count }, (_, i) => (
        <Skeleton
          key={i}
          className="h-10 w-32 rounded-full"
          style={{ animationDelay: `${i * 80}ms` }}
        />
      ))}
    </div>
  )
}

export function PageHeaderSkeleton({ hasButton = false }: { hasButton?: boolean }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <Skeleton className="h-8 w-48 sm:h-10 sm:w-64" />
        <Skeleton className="mt-2 h-4 w-64" />
      </div>
      {hasButton && <Skeleton className="h-10 w-32 rounded-full" />}
    </div>
  )
}

export function CardSkeleton() {
  return (
    <div className="rounded-[10px] border border-gold-500 bg-white p-6 space-y-4">
      <Skeleton className="h-5 w-1/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  )
}

export function DetailPageSkeleton() {
  return (
    <div className="space-y-6">
      {/* Back button and header */}
      <div className="flex items-center gap-4">
        <Skeleton className="h-10 w-10 rounded-lg" />
        <div className="flex-1">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="mt-2 h-4 w-32" />
        </div>
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-gold-500/50">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-6 w-20 mb-3" style={{ animationDelay: `${i * 80}ms` }} />
        ))}
      </div>

      {/* Content grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <CardSkeleton />
          <CardSkeleton />
        </div>
        <div className="space-y-6">
          <CardSkeleton />
        </div>
      </div>
    </div>
  )
}

export function FormFieldSkeleton() {
  return (
    <div className="space-y-1.5">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-10 w-full rounded-lg" />
    </div>
  )
}

export function FormSkeleton({ fields = 4 }: { fields?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: fields }, (_, i) => (
        <FormFieldSkeleton key={i} />
      ))}
    </div>
  )
}

// Page-specific skeletons

export function GivingPageSkeleton() {
  return (
    <div className="space-y-6">
      <PageHeaderSkeleton hasButton />
      <StatCardsSkeleton count={4} />
      <FiltersSkeleton count={3} />
      <TableSkeleton rows={6} columns={6} />
    </div>
  )
}

export function SchoolsPageSkeleton() {
  return (
    <div className="space-y-6">
      <PageHeaderSkeleton hasButton />
      <TabsSkeleton count={2} />
      <TableSkeleton rows={6} columns={6} />
    </div>
  )
}

export function ChildrenPageSkeleton() {
  return (
    <div className="space-y-6">
      <PageHeaderSkeleton hasButton />
      <TabsSkeleton count={3} />
      <FiltersSkeleton count={3} />
      <TableSkeleton rows={8} columns={6} />
    </div>
  )
}

export function SponsorsPageSkeleton() {
  return (
    <div className="space-y-6">
      <PageHeaderSkeleton hasButton />
      <TabsSkeleton count={2} />
      <FiltersSkeleton count={2} />
      <TableSkeleton rows={6} columns={6} />
    </div>
  )
}

export function NominationsPageSkeleton() {
  return (
    <div className="space-y-6">
      <PageHeaderSkeleton />
      <TabsSkeleton count={3} />
      <TableSkeleton rows={6} columns={5} />
    </div>
  )
}

export function ProgramsPageSkeleton() {
  return (
    <div className="space-y-6">
      <PageHeaderSkeleton hasButton />
      <FiltersSkeleton count={2} />
      <TableSkeleton rows={6} columns={5} />
    </div>
  )
}

export function EventsPageSkeleton() {
  return (
    <div className="space-y-6">
      <PageHeaderSkeleton hasButton />
      <TabsSkeleton count={3} />
      <FiltersSkeleton count={2} />
      <TableSkeleton rows={6} columns={5} />
    </div>
  )
}

export function FormsPageSkeleton() {
  return (
    <div className="space-y-6">
      <PageHeaderSkeleton />
      <TabsSkeleton count={3} />
      <FiltersSkeleton count={2} />
      <TableSkeleton rows={6} columns={5} />
    </div>
  )
}

export function MembersPageSkeleton() {
  return (
    <div className="space-y-6">
      <PageHeaderSkeleton hasButton />
      <TableSkeleton rows={6} columns={4} />
    </div>
  )
}

export function RolesPageSkeleton() {
  return (
    <div className="space-y-6">
      <PageHeaderSkeleton hasButton />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="space-y-4 rounded-[10px] border border-gold-500 bg-white p-5">
            <div className="flex items-start justify-between">
              <Skeleton className="h-5 w-28" style={{ animationDelay: `${index * 80}ms` }} />
              <Skeleton className="h-4 w-12 rounded-full" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-3 w-20" />
              <div className="flex flex-wrap gap-1">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-5 w-20 rounded-full" />
                <Skeleton className="h-5 w-14 rounded-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Legacy dark theme skeletons (kept for backwards compatibility)

export function TableSkeletonDark({ rows = 6 }: { rows?: number }) {
  return (
    <div className="overflow-hidden rounded-xl border border-forest-800">
      <div className="border-b border-forest-800 bg-forest-900 px-4 py-3">
        <div className="h-3 w-24 animate-pulse rounded bg-forest-800" />
      </div>
      <div className="divide-y divide-forest-800/60 bg-forest-900/40">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="flex items-center gap-4 px-4 py-3.5">
            <div
              className="h-3 w-1/3 animate-pulse rounded bg-forest-800"
              style={{ animationDelay: `${index * 80}ms` }}
            />
            <div
              className="hidden h-3 w-1/4 animate-pulse rounded bg-forest-800 sm:block"
              style={{ animationDelay: `${index * 80}ms` }}
            />
            <div
              className="ml-auto h-5 w-16 animate-pulse rounded-full bg-forest-800"
              style={{ animationDelay: `${index * 80}ms` }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

export function NotesSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="rounded-lg border border-forest-800 bg-forest-900 p-3">
          <div className="flex items-center justify-between gap-3">
            <div
              className="h-2.5 w-32 animate-pulse rounded bg-forest-800"
              style={{ animationDelay: `${index * 90}ms` }}
            />
            <div
              className="h-2.5 w-20 animate-pulse rounded bg-forest-800"
              style={{ animationDelay: `${index * 90}ms` }}
            />
          </div>
          <div
            className="mt-2.5 h-2.5 w-3/4 animate-pulse rounded bg-forest-800"
            style={{ animationDelay: `${index * 90}ms` }}
          />
        </div>
      ))}
    </div>
  )
}
